import { Component, NgZone, OnInit, ViewChild } from "@angular/core";
import { NURSERIES_SELECTOR } from "../../crossconcern/utilities/properties/selector.property";
import { BaseComponent } from "../base/base.component";
import { NurseryModel } from "../../datastore/entities/nursery.model";
import { AppComponent } from "../app.component";
import { Location } from "@angular/common";
import { AlertUtils } from "../../crossconcern/utilities/utils/alert.utils";
import { NurseryManager } from "../../business/managers/nursery.manager";
import { concatMap, finalize, map } from "rxjs/operators";
import { UserManager } from "../../business/managers/user.manager";
import { GenericService } from "../../crossconcern/services/generic.services";
import { MapsAPILoader, MapTypeStyle } from "@agm/core";
import { GoogleMap } from "@agm/core/services/google-maps-types";
import { OverlayType } from "../../crossconcern/types/overlay.type";
import { Router } from "@angular/router";
import {
    CLICK_EVENT,
    MAP_INITIAL_LATITUDE,
    MAP_INITIAL_LONGITUDE
} from "../../crossconcern/utilities/properties/map.properties";
import { MAP_ROUTE } from "../../crossconcern/utilities/properties/routename.property";
import { DIM_BREAKPOINT_DESKTOP_WIDTH } from "../../crossconcern/utilities/properties/base.property";
import { PhotosphereManager } from "../../business/managers/photosphere.manager";
import { PromotionModel } from "../../datastore/entities/promotion.model";
import { Observable } from "rxjs/internal/Observable";
import { PromotionList } from "../../datastore/entities/promotionlist.model";
import { MediaList } from "../../datastore/entities/medialist.model";
import { forkJoin } from "rxjs/internal/observable/forkJoin";

declare const google;

@Component({
    selector: NURSERIES_SELECTOR,
    styles: [],
    templateUrl: "nurseries.component.html"
})
export class NurseriesComponent extends BaseComponent implements OnInit {

    private googleMap: GoogleMap;
    public drawingMode: OverlayType;
    public mapStyle: MapTypeStyle[];
    public avgLat = MAP_INITIAL_LATITUDE;
    public avgLong = MAP_INITIAL_LONGITUDE;
    public zoom = 7;
    private NURSERY_MARKER: google.maps.ReadonlyIcon;

    public isSidebarOpen = true;
    public sidebarMode = "push";
    @ViewChild("sidebar", null) private sidebarElement;

    public nurseries: NurseryModel[] = [];
    public tmpPromotions: PromotionModel[] = [];
    public promotions: PromotionModel[] = [];

    constructor(
        protected appComponent: AppComponent,
        protected router: Router,
        protected location: Location,
        protected genericService: GenericService,
        protected mapsAPILoader: MapsAPILoader,
        private nurseryManager: NurseryManager,
        private photosphereManager: PhotosphereManager,
        private userManager: UserManager,
        public zone: NgZone,
        protected alertUtils: AlertUtils) {
        super(appComponent, router, alertUtils, location);
    }

    ngOnInit(): void {
        this.appComponent.startLoading();
        this.isSidebarOpen = window.innerWidth > DIM_BREAKPOINT_DESKTOP_WIDTH;
    }

    public initDrawManager(googleMap: GoogleMap) {
        this.mapsAPILoader.load().then(() => {
            this.NURSERY_MARKER = {
                url: "/assets/img/ic_nursery_marker.png",
                scaledSize: new google.maps.Size(
                    40,
                    60
                )
            };
            this.googleMap = googleMap;
            this.getNurseries();
            this.getFeaturedPromotions();
        });
    }

    private getNurseries() {
        this.nurseryManager.getNurseries(true, 10000, 0)
            .pipe(
                map(([
                    _, nurseries
                ]) => {
                    nurseries.forEach((nursery) => {
                        this.imageExists("/assets/img/slider/" + nursery.uuid + ".jpg", (res) => {
                            if(res) {
                                nursery.imageURL = "/assets/img/slider/" + nursery.uuid + ".jpg";
                            } else {
                                nursery.imageURL = null;
                            }
                        });
                    });
                    return nurseries;
                }),
                finalize(() => this.appComponent.stopLoading())
            )
            .subscribe((list) => {
                this.nurseries = list;
                this.addMarkers();
            });
    }

    private addMarkers() {
        if(this.nurseries) {
            for(const nursery of this.nurseries) {
                if(nursery.latitude && nursery.longitude) {
                    const content =
                        "<div>" +
                            "<h6>" + nursery.name + "</h6>" +
                        "</div>";
                    const infoWindow = new google.maps.InfoWindow({
                        position: new google.maps.LatLng(nursery.latitude, nursery.longitude),
                        content,
                        pixelOffset: new google.maps.Size(0, -60)
                    });

                    const marker = new google.maps.Marker({
                        position: new google.maps.LatLng(nursery.latitude, nursery.longitude),
                        icon: this.NURSERY_MARKER
                    });
                    marker.setMap(this.googleMap);
                    google.maps.event.addListener(marker, CLICK_EVENT, () => {
                        this.goToRouteByName(MAP_ROUTE, { id: nursery.uuid });
                    });
                    google.maps.event.addListener(marker, "mouseover", () => {
                        infoWindow.open(this.googleMap);
                    });
                    google.maps.event.addListener(marker, "mouseout", () => {
                        infoWindow.close();
                    });
                }
            }
        }
    }

    private imageExists(url, callback) {
        const img = new Image();
        img.onload = function() {
            callback(true);
        };
        img.onerror = function() {
            callback(false);
        };
        img.src = url;
    }

    private getFeaturedPromotions() {
        this.photosphereManager.getPromotions(10000, 0, null, true)
            .pipe(
                concatMap((promotionList: PromotionList) => {
                    this.tmpPromotions = promotionList.promotions;
                    const promotionsMediaList: Observable<MediaList>[] = [];
                    this.tmpPromotions.forEach((promotion) => {
                        promotionsMediaList.push(this.photosphereManager.getPromotionMedia(promotion.uuid));
                    });
                    return forkJoin(...promotionsMediaList);
                }),
                finalize(() => this.appComponent.stopLoading())
            )
            .subscribe((medias: MediaList[]) => {
                medias.forEach((media, index) => {
                    if(media.medias && media.medias.length > 0) {
                        const mediaURL = media.medias[0].getMediaURL("view_fhd");
                        if(mediaURL) {
                            this.tmpPromotions[index].mediaURL = mediaURL.replace("{?res}", "");
                        }
                    }
                });
                this.promotions = this.tmpPromotions;
            });
    }

    // Sidebar

    public toggleSidebar() {
        this.isSidebarOpen = !this.isSidebarOpen;
    }

    public refreshSidebar() {
        setTimeout(() => {
            this.sidebarElement.triggerRerender();
        }, 900);
    }
}